import React, { FC, useCallback, useMemo, useState } from 'react';
import { Breadcrumbs, Button, Chip, Tooltip, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { MaterialSymbol } from '@/components/MaterialSymbol';
import CustomIcon from '@/components/CustomIcon';
import { PageStatusCircle } from '@/components/PageStatusCircle';
import { EventData, Page, TimePeriod } from '@/declarations/models/Page';
import { formatAPITimestamp, isApiTimestampAtOrAfter } from '@/utils/dates';
import { Status } from '@/declarations/models/Status';
import { Api } from '@/services/Api';
import { useStore } from '@/components/store/Store';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LockIcon from '@mui/icons-material/Lock';
import PageTreeActionMenu from './PageTreeActionMenu';
import Container from '../../../components/Container';
import PageTreeCheckbox from './PageTreeCheckbox';
import PageTreeLabel from './PageTreeLabel';
import { useContentView } from '../ContentViewContext';
import { rememberLastPages } from '../lastPage';
import Styles from '../../../assets/js/Styles';
import Image from '../../../components/Image';

export interface PageTreeContentProps {
  page: Page;
  diffuse?: boolean;
  displayBreadcrumbs?: boolean;
  hideBorder?: boolean;
  /** One of the pages created or moved while in the page overview */
  marked?: boolean;
}

type DisplayStatus = Status | 'planned_publish';

function formatDatePeriod(tp: TimePeriod) {
  let result = formatAPITimestamp(tp.start_date, 'date');
  if (tp.end_date) {
    result += ` - ${formatAPITimestamp(tp.end_date, 'date')}`;
  }
  if (tp.start_time) {
    result += ` ${formatAPITimestamp(tp.start_time, 'time')}`;
  }
  if (tp.end_time) {
    result += ` - ${formatAPITimestamp(tp.end_time, 'time')}`;
  }
  return result;
}

const getDateText = (eventData: EventData) => {
  const datesLen = eventData.time_periods?.length ?? 0;

  if (!eventData.time_periods || datesLen === 0) {
    return '';
  }

  if (datesLen === 1) {
    return formatDatePeriod(eventData.time_periods[0]);
  }

  return `${formatDatePeriod(eventData.time_periods[0])} (+${datesLen - 1})`;
};

const getDisplayStatus = (page: Page): DisplayStatus => {
  if (page.status === Status.PUBLISHED) {
    const isPlannedPublish = !!page.published_at && isApiTimestampAtOrAfter(page.published_at);

    if (isPlannedPublish) {
      return 'planned_publish';
    }
  }

  return page.status;
};

const getDisplayStatusText = (displayStatus: DisplayStatus, tCommon: (key: string) => string): string => {
  if (displayStatus === 'planned_publish') {
    return tCommon('Status.planned_publishing');
  }

  if (displayStatus === Status.PUBLISHED) {
    return tCommon('Status.published');
  }

  if (displayStatus === Status.DRAFT) {
    return tCommon('Status.not_published');
  }

  if (displayStatus === Status.ARCHIVED) {
    return tCommon('Status.archived');
  }

  if (displayStatus === Status.IMPORTED) {
    return tCommon('Status.imported');
  }

  return '';
};

export const PageTreeContent: FC<PageTreeContentProps> = ({
  page,
  diffuse,
  displayBreadcrumbs = false,
  hideBorder = false,
  marked = false,
}) => {
  const { t: tCommon } = useTranslation('common');
  const { t: tComp } = useTranslation('components');
  const navigate = useNavigate();
  const {
    selectionMode,
    openCreatePageModal,
    selectionState,
    movePagesMode,
    setMovePagesMode,
    setSelectionMode,
    hasDraft,
    filterValues,
    setBulkLoading,
    reloadPages,
    showImported,
    setMarkedPageIds,
  } = useContentView();
  const { getParents } = selectionState;
  const { enqueueSnackbar } = useSnackbar();
  const { state } = useStore();
  const selectedSiteId = state.selectedSite?.id || 0;
  const breadcrumbs = getParents(page.id || 0)?.map((parent) => parent.title);
  const selectedPageIDs = selectionState.selectedItems?.map((p) => p.id || 0);
  const isEvent = page.main_category === 'event';
  const imageUrl = page.image_src;
  const isFrontpage = page.id === state.selectedSiteDefaultPageId;

  const displayStatus = useMemo(() => getDisplayStatus(page), [page]);
  const pageStatusText = useMemo(() => getDisplayStatusText(displayStatus, tCommon), [displayStatus, tCommon]);

  const [focused, setFocused] = useState<boolean>(false);

  const handleMovePages = useCallback(async (): Promise<Array<Page> | void> => {
    setBulkLoading(true);
    try {
      const result = await Api.bulkEditPages(selectedSiteId, {
        item_ids: selectedPageIDs,
        parent_id: page.id,
        item_type: 'page',
      }).fetchDirect(null);
      if (!result) {
        enqueueSnackbar(tCommon('saveFailed'), { variant: 'error' });
      } else {
        enqueueSnackbar(tCommon('saved'), { variant: 'success' });
        rememberLastPages(selectedSiteId, selectedPageIDs);
        setMarkedPageIds(selectedPageIDs);
        return result.changed;
      }
    } finally {
      setBulkLoading(false);
    }
  }, [enqueueSnackbar, page.id, selectedPageIDs, selectedSiteId, tCommon, setBulkLoading, setMarkedPageIds]);

  let statusColor: string;

  if (displayStatus === 'planned_publish') {
    statusColor = Styles.Colors.STRONG_ORANGE;
  } else if (displayStatus === Status.PUBLISHED) {
    statusColor = 'green';
  } else {
    statusColor = 'textSecondary';
  }

  return (
    <Container
      onMouseOver={() => setFocused(true)}
      onMouseLeave={() => setFocused(false)}
      sx={(theme) => ({
        py: 1,
        // Every title a little in, so a marked row's outline does not start right at it
        pl: 1,
        opacity: diffuse ? 0.5 : 1,
        // Same colour as a pasted section in the page editor, thinner to suit the tree's lines
        ...(marked && {
          outline: `2px solid ${Styles.Colors.COPY_PASTE_PURPLE_LIGHT}`,
          outlineOffset: '-2px',
        }),
        borderBottom:
          hideBorder || displayBreadcrumbs || isEvent
            ? undefined
            : `1px solid ${focused ? theme.palette.text.disabled : theme.palette.divider}`,
        height: 'auto',
      })}
      spaceBetween
      fullWidth>
      <Container left fullWidth gap={0}>
        {!isFrontpage && selectionMode && <PageTreeCheckbox pageId={page.id || 0} />}
        <Container left column pl={isEvent ? 2 : 0} gap={0}>
          <PageTreeLabel page={page} />
          {displayBreadcrumbs && (
            <Breadcrumbs
              separator={<MaterialSymbol name='chevron_right' />}
              sx={{
                '& .MuiBreadcrumbs-separator': {
                  mx: 0,
                },
              }}>
              {breadcrumbs?.map((breadcrumb) => (
                <Typography
                  key={breadcrumb}
                  variant='subtitle2'
                  sx={{ fontSize: 'small' }}
                  color='text.secondary'
                  noWrap>
                  {breadcrumb}
                </Typography>
              ))}
              <Typography variant='body2' color='text.primary' sx={{ fontSize: 'small' }} noWrap>
                {page.title}
              </Typography>
            </Breadcrumbs>
          )}
          {isEvent && (
            <Container>
              <CustomIcon name='calendar_event' />
              <Typography variant='subtitle2' color='text.secondary' noWrap>
                {!page.event_data?.time_periods?.length
                  ? tComp('PageTreeContent.missingDate')
                  : getDateText(page.event_data)}
              </Typography>
              {page.event_data?.use_range &&
                page.event_data.week_days &&
                page.event_data.week_days.map((d) => (
                  <Chip key={d} size='small' label={tCommon(`WeekDay.${d}`)} sx={{ height: '20px' }} />
                ))}
            </Container>
          )}
        </Container>
        <Container ml={5} right>
          {!isEvent && page.main_category && (
            <Chip
              variant='outlined'
              color='secondary'
              size='small'
              label={tCommon(`MainCategory.${page.main_category}`)}
            />
          )}
          {page.sub_categories?.slice(0, 1).map((c) => (
            <Chip key={c} variant='outlined' color='secondary' size='small' label={tCommon(`SubCategory.${c}`)} />
          ))}

          {page.sub_categories && page.sub_categories?.length > 1 ? (
            <Chip
              variant='outlined'
              color='secondary'
              size='small'
              label={`+ ${(page.sub_categories?.length || 1) - 1}`}
            />
          ) : null}
          {page.location && <Chip variant='filled' color='secondary' size='small' label={page.location.title} />}
        </Container>
      </Container>
      {focused && (
        <Container right mr={3} fullWidth wrap gap={1}>
          {page.id && movePagesMode && !selectedPageIDs?.includes(page.id) && (
            <Button
              disabled={selectionState.noneSelected}
              variant='outlined'
              sx={{
                boxShadow: undefined,
                px: 3,
                whiteSpace: 'nowrap',
                backgroundColor: Styles.Colors.LIGHT_GREY,
                border: `1px solid ${Styles.Colors.MEDIUM_LIGHT_GREY}`,
                minWidth: 'fit-content',
              }}
              onClick={() =>
                handleMovePages().then((savedPages) => {
                  if (savedPages) {
                    selectionState.deselectAll();
                    setMovePagesMode(false);
                    setSelectionMode(false);
                    reloadPages();
                  }
                })
              }>
              {tComp('PageTreeContent.movePagesHereButtonLabel')}
            </Button>
          )}
          {!movePagesMode && (
            <>
              <Button
                variant='contained'
                sx={{
                  boxShadow: undefined,
                  px: 3,
                  whiteSpace: 'nowrap',
                  border: `1px solid ${Styles.Colors.MEDIUM_LIGHT_GREY}`,
                  minWidth: 'fit-content',
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/editor/${page.id}`);
                }}
                startIcon={<MaterialSymbol name='edit' fill />}>
                {tComp(`PageTreeContent.edit${isEvent ? 'Event' : 'Page'}ButtonLabel`)}
              </Button>
              {!isEvent && !filterValues.lastEditedPagesView && !showImported && (
                <Button
                  variant='contained'
                  color='success'
                  sx={{
                    px: 3,
                    whiteSpace: 'nowrap',
                    minWidth: 'fit-content',
                  }}
                  onClick={(e) => {
                    // Not a click on the tree item: the page stays open or closed, and opens when the subpage is made
                    e.stopPropagation();
                    openCreatePageModal(page);
                  }}
                  startIcon={<MaterialSymbol name='add' />}>
                  {tComp('PageTreeContent.addSubPageButtonLabel')}
                </Button>
              )}
            </>
          )}
        </Container>
      )}
      <Container right>
        {page.is_protected === true && (
          <Container>
            <LockIcon
              sx={{
                fontSize: '14px',
                color: page.locked_by_parent ? 'text.disabled' : 'inherit',
                opacity: page.locked_by_parent ? 0.7 : 1,
              }}
            />
          </Container>
        )}
        {page.status === Status.PUBLISHED && !!page.published_at && isApiTimestampAtOrAfter(page.published_at) && (
          <Container>
            <AccessTimeIcon />
          </Container>
        )}
        <Container column left sx={{ minWidth: '220px' }} gap={0}>
          <Container>
            <PageStatusCircle status={displayStatus} />
            <Typography variant='caption' color={statusColor}>
              {pageStatusText}{' '}
              {(page.status === Status.PUBLISHED && formatAPITimestamp(page.published_at || '', 'date')) || ''}
            </Typography>

            {hasDraft(page.id!) && (
              <Tooltip title={tComp('PageTreeContent.HasDraftVersion')}>
                <Typography variant='caption' sx={{ display: 'flex', alignItems: 'center' }}>
                  [ <MaterialSymbol name='format_paint' sx={{ mx: 0, fontSize: 'small' }} /> ]
                </Typography>
              </Tooltip>
            )}
          </Container>
          <Container column left gap={0} mt={0} ml={4}>
            <Typography variant='caption' color='textSecondary' noWrap>
              {tCommon('lastEdited')}: {formatAPITimestamp(page.updated_at || '', 'date') || '-'}
            </Typography>
          </Container>
        </Container>
        <Container>
          <Image
            sx={{
              overflow: 'hidden',
              objectFit: 'cover',
              height: '44px',
              flex: 1,
              width: '44px',
              borderRadius: Styles.Dimensions.RADIUS_ROUND_FULL,
              border: `1px solid ${Styles.Colors.MEDIUM_LIGHT_GREY}`,
              mr: 2,
              backgroundColor: Styles.Colors.LIGHT_GREY,
            }}
            src={`${imageUrl}?dimension=250x250${page.image?.mimetype?.includes('png') ? '&mediaType=image/png' : ''}`}
          />
        </Container>
        {!movePagesMode && (
          <PageTreeActionMenu
            page={page}
            onClose={() => {
              setFocused(false);
            }}
          />
        )}
      </Container>
    </Container>
  );
};

export default PageTreeContent;
